import { z } from 'zod';
import { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import CasFormInput from '@cas/components/inputs/cas-form-input/CasFormInput';
import { ModelName } from '@casops/store/api';
import ModelForm from '@casops/components/layout/model-form/ModelForm';
import useModelForm, { CanSubmitProps } from '@casops/components/layout/model-form/useModelForm';
import {
  CreateGeofenceSpeedLimitModel,
  EditGeofenceSpeedLimitModel,
  GeofenceSpeedLimitModel,
  GeofenceSpeedLimitFormModel,
  useCreateGeofenceSpeedLimitMutation,
  useEditGeofenceSpeedLimitMutation,
} from '@casops/store/apis/geofenceSpeedLimitsApi';
import { ServerErrorFields } from '@dmc/ui';

type DataModel = GeofenceSpeedLimitModel;
type FormModel = GeofenceSpeedLimitFormModel;
type CreateModel = CreateGeofenceSpeedLimitModel;
type EditModel = EditGeofenceSpeedLimitModel;

const modelName = ModelName.GeofenceSpeedLimit;

const defaultValues: FormModel = {
  speed: 1,
  desc: '',
};

// Allow an untouched (!isAnyFieldDirty) Create form to be submitted since all default values are valid.
const canSubmitCallback = ({
  isSubmitting,
  isAnyFieldDirty,
  isValid,
  editMode,
}: CanSubmitProps): boolean => {
  return !isSubmitting && isValid && (isAnyFieldDirty || !editMode);
};

const serverErrorFields: ServerErrorFields<FormModel> = {
  speed: 'Speed',
  desc: 'Description',
};

const MIN_SPEED = 1;
const MAX_SPEED = 500;
const DESCRIPTION_MAX = 255;

interface SpeedLimitFormProps {
  item?: DataModel;
}

export const SpeedLimitForm = ({ item }: SpeedLimitFormProps): ReactElement => {
  const { t } = useTranslation();

  const schema = useMemo(() => {
    const speedOutOfRangeMessage = t('Validation.SpeedOutOfRange', {
      min: MIN_SPEED,
      max: MAX_SPEED,
    });

    return z.object({
      speed: z
        .number()
        .min(MIN_SPEED, { message: speedOutOfRangeMessage })
        .max(MAX_SPEED, { message: speedOutOfRangeMessage })
        .int({ message: t('Validation.ValueMustBeInteger') }),
      desc: z.string().max(DESCRIPTION_MAX, {
        message: t('Validation.DescriptionTooLong', { max: DESCRIPTION_MAX }),
      }),
    });
  }, [t]);

  const [triggerCreateItem] = useCreateGeofenceSpeedLimitMutation();
  const [triggerEditItem] = useEditGeofenceSpeedLimitMutation();

  const { canSubmit, onSubmit, errorMessage, control } = useModelForm<
    DataModel,
    FormModel,
    CreateModel,
    EditModel
  >({
    modelName,
    defaultValues,
    item,
    schema,
    triggerCreateItem,
    triggerEditItem,
    serverErrorFields,
    canSubmitCallback,
  });

  return (
    <ModelForm
      canSubmit={canSubmit}
      onSubmit={onSubmit}
      errorMessage={errorMessage}
      control={control}
    >
      <>
        <CasFormInput
          key="speed"
          control={control}
          name="speed"
          label={t('Fields.Speed')}
          type="number"
        />
        <CasFormInput
          key={'desc'}
          control={control}
          name="desc"
          label={t('Fields.Description')}
          element="textarea"
          minHeight="100px"
        />
      </>
    </ModelForm>
  );
};

export default SpeedLimitForm;
